import React from 'react';
import { Search, MapPin, Bed, Bath, Square, TrendingUp, Home } from 'lucide-react';
import { IDX_BASE_URL, PROPERTY_1, PROPERTY_2 } from '../../constants/site';
import { IdxSearchTool } from './IdxSearchTool';
import { scrollToSection } from '../../utils/scroll';

const FEATURED_PROPERTIES = [
  { img: PROPERTY_1, price: '$11,000,000', loc: '101 Station One Lane, Corolla, NC (Pine Island)', title: 'Record-Breaking Outer Banks Estate', desc: 'Most expensive home in OBX history! 250ft private beach, saltwater pool, home theater, sauna, 5-hole putting green, 360° views', bed: '9', bath: '9', sqft: '12,000+ sqft' },
  { img: PROPERTY_2, price: '$9,500,000', loc: '1501 Oak Hill Court, Virginia Beach (Linkhorn Bay)', title: 'Luxury Waterfront Estate', desc: 'Extraordinary estate on 3.7-acre peninsula with resort-style pool, outdoor pavilion, and private beach access', bed: '7', bath: '8', sqft: '10,835 sqft' },
];

const PropertyCard = ({ p }) => (
  <div className="group bg-sand-50 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-blue-100/50 hover:border-blue-300/50">
    <div className="relative h-72 overflow-hidden">
      <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" decoding="async" />
      <div className="absolute top-4 right-4 gold-gradient px-5 py-2 rounded-xl shadow-2xl"><span className="font-bold text-white text-lg">{p.price}</span></div>
    </div>
    <div className="p-6 space-y-4">
      <div>
        <div className="flex items-center space-x-2 text-blue-600 mb-2"><MapPin className="w-4 h-4" /><span className="text-sm font-semibold">{p.loc}</span></div>
        <h3 className="text-2xl font-serif font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{p.title}</h3>
      </div>
      <p className="text-gray-600 text-sm leading-relaxed">{p.desc}</p>
      <div className="flex items-center justify-between pt-4 border-t-2 border-blue-100">
        <div className="flex items-center space-x-2 text-gray-700"><Bed className="w-5 h-5 text-blue-500" /><span className="text-sm font-bold">{p.bed}</span></div>
        <div className="flex items-center space-x-2 text-gray-700"><Bath className="w-5 h-5 text-blue-500" /><span className="text-sm font-bold">{p.bath}</span></div>
        <div className="flex items-center space-x-2 text-gray-700"><Square className="w-5 h-5 text-blue-500" /><span className="text-sm font-bold">{p.sqft}</span></div>
      </div>
      <button onClick={() => scrollToSection('contact')} className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-3 rounded-md font-medium flex items-center justify-center">Schedule Viewing<TrendingUp className="w-4 h-4 ml-2" /></button>
    </div>
  </div>
);

const SearchAllPanel = () => (
  <div className="mt-16 bg-gradient-to-br from-[#143160] to-[#1E4A86] rounded-3xl p-10 shadow-2xl">
    <div className="text-center max-w-2xl mx-auto">
      <h3 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-4">Search All Properties</h3>
      <p className="text-white/80 mb-8 text-lg">Access thousands of homes across Virginia Beach, Norfolk, Chesapeake, Sandbridge, Corolla, Duck, Nags Head and every coastal community in Virginia & North Carolina.</p>
      <div className="max-w-xl mx-auto mb-6">
        <IdxSearchTool />
      </div>
      <div className="flex flex-wrap items-center justify-center gap-4">
        <a href={IDX_BASE_URL} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/30 rounded-full text-white font-semibold transition-all"><Home className="w-5 h-5" /><span>All Listings</span></a>
      </div>
    </div>
  </div>
);

export const PropertiesSection = () => (
  <section id="properties" className="py-16 sm:py-24 bg-sand-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-16 space-y-6">
        <div className="inline-flex items-center space-x-3 px-6 py-3 bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-200/60 rounded-full shadow-lg">
          <Search className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-bold text-gray-700 tracking-wide uppercase">Featured Properties</span>
        </div>
        <h2 className="text-5xl sm:text-6xl font-serif font-bold leading-tight">Discover Your<span className="block gold-gradient-text">Dream Property</span></h2>
        <p className="text-xl text-gray-600 leading-relaxed">Explore a curated selection of exceptional properties along the beautiful Virginia and North Carolina coast.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mb-12">
        {FEATURED_PROPERTIES.map((p) => <PropertyCard key={p.title} p={p} />)}
      </div>
      <SearchAllPanel />
    </div>
  </section>
);
